import type { QueryClient, QueryKey } from '@tanstack/react-query'
import type { RealtimeChannel } from '@supabase/supabase-js'

/**
 * Live updates for shared pages: when a row in one of these tables changes,
 * the listed queries are marked stale, so whoever has them on screen refetches
 * and everyone else refetches when they next look. Each key is a prefix.
 *
 * Every table here must be in the `supabase_realtime` publication
 * (20261101100000, 20261102180000), or its listener silently hears nothing.
 * Realtime checks each change against the subscriber's RLS, so a client only
 * hears about rows they could select anyway.
 *
 * Chats and the support inbox are not here: their handlers in CrmUnreadContext
 * also raise notifications and narrow the keys by thread.
 */
const DELETABLE: QueryKey[] = [
  ['tasks'],
  ['tasks-paged'],
  ['calendar'],
  ['projects'],
  ['my-projects'],
  ['files'],
  ['files-paged'],
  ['clients'],
  ['clients-paged'],
  ['client-notes'],
  ['project-notes'],
  ['orders'],
  ['orders-paged'],
  ['crm-badge-counts'],
]

export const LIVE_TABLES: Record<string, QueryKey[]> = {
  tasks: [['tasks'], ['tasks-paged'], ['calendar'], ['projects'], ['my-projects'], ['team'], ['crm-badge-counts']],
  task_series: [['tasks']],
  // Task and file lists show project names.
  projects: [['projects'], ['my-projects'], ['calendar'], ['tasks'], ['files']],
  project_employees: [['projects'], ['my-projects'], ['team'], ['calendar']],
  project_notes: [['project-notes']],
  client_notes: [['client-notes']],
  clients: [['clients'], ['clients-paged'], ['my-client'], ['companies']],
  attachments: [['files'], ['files-paged'], ['deliveries'], ['my-deliveries']],
  // A quote sent, a date confirmed or a delivery sent shows without a reload,
  // on both sides. The client's lists and staff's share these keys; each only
  // refetches where it is loaded.
  orders: [['my-orders'], ['orders'], ['orders-paged'], ['orders-waiting-review-count']],
  // A delivery being sent is what makes its files readable to the client.
  deliveries: [['my-deliveries'], ['deliveries'], ['files']],
  // A client added to a delivery already out sees it appear.
  delivery_recipients: [['my-deliveries'], ['files']],
  // Binning a row hides it from RLS, so Realtime never sends that change; the
  // recycle bin bumps this staff-only row instead (20261102180000).
  live_signals: [['recycle-bin'], ...DELETABLE],
}

const BATCH_MS = 300

/**
 * Adds a listener per table in LIVE_TABLES to `channel`. Returns `catchUp`,
 * which marks every key stale (for after a dropped connection, when changes
 * went undelivered), and `dispose`, which drops a pending batch.
 *
 * Changes are batched: a series edit or a project binned with its tasks is
 * dozens of rows, and each refetch would cancel the one before. A batch also
 * waits while a mutation is in flight, so a refetch does not overwrite an
 * optimistic edit before its own invalidation settles it.
 */
export function attachLiveTables(channel: RealtimeChannel, qc: QueryClient) {
  const pending = new Map<string, QueryKey>()
  let timer: ReturnType<typeof setTimeout> | undefined

  const flush = () => {
    timer = undefined
    if (qc.isMutating() > 0) {
      timer = setTimeout(flush, BATCH_MS)
      return
    }
    const keys = [...pending.values()]
    pending.clear()
    for (const queryKey of keys) void qc.invalidateQueries({ queryKey })
  }

  const queue = (keys: QueryKey[]) => {
    for (const key of keys) pending.set(JSON.stringify(key), key)
    timer ??= setTimeout(flush, BATCH_MS)
  }

  for (const [table, keys] of Object.entries(LIVE_TABLES)) {
    channel.on('postgres_changes', { event: '*', schema: 'public', table }, () => queue(keys))
  }

  return {
    catchUp: () => queue(Object.values(LIVE_TABLES).flat()),
    dispose: () => {
      if (timer) clearTimeout(timer)
      pending.clear()
    },
  }
}
